import { Banner, ControlledDateOnlyPicker, FieldGrid, FormMethods, useFormDialog } from '@if/mui'
import { Stack } from '@mui/material'
import { getToday } from '@src/services/DateTimeProvider'
import {
  CalendarSimulationAction,
  ECalendarSimulationAction,
  SignedCalendarSimulation,
} from '@src/types/loan/loan-simulation'
import { FileX } from 'lucide-react'
import { useMemo } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCalendarPreviewConfirm } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-confirm'
import {
  useLoanCalendarActionPreview,
  useSettledValue,
} from '../LoanCalendarActionPreview/LoanCalendarActionPreview-recompute'
import LoanCalendarActionPreviewBody from '../LoanCalendarActionPreview/LoanCalendarActionPreviewBody'
import { useChargeOffSchema, ChargeOffSchemaType } from './LoanCalendarChargeOff-schema'

interface LoanCalendarChargeOffBodyProps {
  form: FormMethods<ChargeOffSchemaType>
  initialSignedSimulation: SignedCalendarSimulation
  disabled: boolean
}

const LoanCalendarChargeOffBody = ({ form, initialSignedSimulation, disabled }: LoanCalendarChargeOffBodyProps) => {
  const { t } = useTranslation()
  const { control } = form

  const previewedSimulation = useWatch({ control, name: 'previewedSimulation' })
  const effectiveDate = useWatch({ control, name: 'effectiveDate' })
  const { isValid } = useFormState({ control })

  const settledEffectiveDate = useSettledValue(effectiveDate)

  const action = useMemo<CalendarSimulationAction | null>(
    () =>
      isValid && settledEffectiveDate
        ? { actionType: ECalendarSimulationAction.ChargeOff, effectiveDate: settledEffectiveDate }
        : null,
    [isValid, settledEffectiveDate],
  )

  const error = useLoanCalendarActionPreview({ form, initialSignedSimulation, action })

  return (
    <Stack spacing={2}>
      <LoanCalendarActionPreviewBody
        simulationBefore={initialSignedSimulation.simulation}
        preview={previewedSimulation}
        recomputing={isValid && previewedSimulation === null}
        actionDate={effectiveDate}
        error={error}
      />

      <FieldGrid columns={{ xs: 1 }}>
        <ControlledDateOnlyPicker
          control={control}
          name="effectiveDate"
          label={t('loan.payment.chargeOffDialog.effectiveDate')}
          // A charge-off records a loss that already happened; the backend refuses a future date too.
          maxDate={getToday()}
          disabled={disabled}
        />
      </FieldGrid>

      <Banner severity="warning">{t('loan.payment.chargeOffDialog.warning')}</Banner>
    </Stack>
  )
}

export const useLoanCalendarChargeOffDialog = (
  initialSignedSimulation: SignedCalendarSimulation,
  isLockedByOther: boolean,
) => {
  const { t } = useTranslation()
  const schema = useChargeOffSchema()
  const confirm = useCalendarPreviewConfirm(isLockedByOther)

  return useFormDialog<ChargeOffSchemaType, void, SignedCalendarSimulation>({
    icon: FileX,
    title: t('loan.payment.chargeOffDialog.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema,
    // Confirming stages a pending change on the page, it saves nothing — the page says so.
    successMessage: false,
    ...confirm,
    content: ({ form }) => (
      <LoanCalendarChargeOffBody
        form={form}
        initialSignedSimulation={initialSignedSimulation}
        disabled={isLockedByOther}
      />
    ),
    onSubmit: (values) => values.previewedSimulation ?? false,
  })
}
